import { useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import useFoodware from 'hooks/use-foodware';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { useOrderContext } from 'state/order';
import { IPendingReorder } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';
import { isDelivery } from 'utils/service-mode';

import { isOfferType } from '../utils';

export const useReorder = (order?: IPendingReorder | null) => {
  const {
    reorder: { handleReorder, setPendingReorder, setShouldHandleReorder },
    isPreConfirmCurbside,
  } = useOrderContext();
  const { isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const { setShouldShowPreConfirmCurbsideModal } = useUIContext();
  const enableReorder = useFlag(LaunchDarklyFlag.ENABLE_REORDER);
  const { isFoodware } = useFoodware();
  const router = useRouter();

  // Temporary flag to disable reordering of items/combos based on Sanity IDs.
  // Should be removed once One Menu is live.
  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const isExcludedFromReordering = useMemo(() => {
    if (!order?.cart) {
      return false;
    }

    const cartEntries = order.cart.cartEntries || [];

    // If there's only one item and it's excluded from reordering, return true
    if (cartEntries.length === 1) {
      const singleEntry = cartEntries[0];
      if ((sanityIdsExcludedFromReordering ?? []).includes(singleEntry.sanityId)) {
        return true;
      }
    }

    // Otherwise only check for offers
    return cartEntries.some((entry: IBackendCartEntries) => isOfferType(entry.type));
  }, [order, sanityIdsExcludedFromReordering]);

  const handleReorderClick = useCallback(() => {
    if (isPreConfirmCurbside()) {
      setShouldShowPreConfirmCurbsideModal(true);
      return;
    }
    if (!order) {
      return;
    }

    const filteredReorderCartEntries = order.cart.cartEntries?.filter(item => {
      const isExcludedType = item.isDonation || isFoodware(item);
      const isExcludedSanityId = (sanityIdsExcludedFromReordering ?? []).includes(item.sanityId);

      return !(isExcludedType || isExcludedSanityId);
    });

    const filteredOrder: IPendingReorder = {
      ...order,
      cart: {
        ...order.cart,
        reorderCartEntries: filteredReorderCartEntries,
      },
    };

    if (isStoreOpenAndAvailable) {
      handleReorder(filteredOrder);
    } else {
      setPendingReorder(filteredOrder);
      setShouldHandleReorder(true);
      router.navigate(isDelivery(serviceMode) ? routes.deliveryAddress : routes.storeLocator);
    }
  }, [
    isPreConfirmCurbside,
    order,
    isStoreOpenAndAvailable,
    setShouldShowPreConfirmCurbsideModal,
    isFoodware,
    sanityIdsExcludedFromReordering,
    handleReorder,
    setPendingReorder,
    setShouldHandleReorder,
    router,
    serviceMode,
  ]);

  const disabled =
    !enableReorder || isExcludedFromReordering || (!isStoreOpenAndAvailable && !noStoreSelected);
  const disableReorder = useMemo(() => disabled, [disabled]);

  return { handleReorderClick, disableReorder };
};
